import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AlertType, DataminrAlertDetail, DataminrAlertFeatureCollection, DataminrSortField, DataminrSortOrder } from './types';
import { sec } from './security';

const { VITE_DATAMINR_SERVICE_HOST } = import.meta.env;

/** Query accepted by /v0/alerts and /v0/alerts/geojson. Every field is optional. */
export interface DataminrAlertsQuery {
  /** Free text over headline and public post text. Required when sort is 'relevance'. */
  q?: string;
  alert_types?: AlertType[];
  topic_ids?: string[];
  list_ids?: string[];
  event_id?: string;
  terms?: string[];
  /** ISO-8601; inclusive lower bound. */
  since?: string;
  /** ISO-8601; exclusive upper bound. */
  until?: string;
  /** minLng,minLat,maxLng,maxLat — the API also accepts nested arrays. */
  bounding_box?: string;
  has_location?: boolean;
  sort?: DataminrSortField;
  order?: DataminrSortOrder;
  limit?: number;
  offset?: number;
}

/**
 * Array filters go over the wire comma-separated, which is what the API's
 * parseCommaSeparated / parseStringArray accept for both GET and POST.
 */
const serializeQuery = ({ alert_types, topic_ids, list_ids, terms, ...rest }: DataminrAlertsQuery): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== null && value !== '') params[key] = value as string | number | boolean;
  }
  if (alert_types?.length) params.alert_types = alert_types.join(',');
  if (topic_ids?.length) params.topic_ids = topic_ids.join(',');
  if (list_ids?.length) params.list_ids = list_ids.join(',');
  if (terms?.length) params.terms = terms.join(',');

  return params;
};

export const dataminrService = createApi({
  reducerPath: 'dataminrService',
  baseQuery: fetchBaseQuery({
    baseUrl: VITE_DATAMINR_SERVICE_HOST,
    prepareHeaders: async (headers) => {
      const access_token = await sec.getAccessTokenSilently()();
      if (access_token) headers.set('Authorization', `Bearer ${access_token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getDataminrAlerts: builder.query<DataminrAlertFeatureCollection, DataminrAlertsQuery>({
      query: (params) => ({
        url: '/v0/alerts/geojson',
        params: serializeQuery(params)
      })
    }),
    getDataminrAlert: builder.query<DataminrAlertDetail, { alert_id: string }>({
      query: ({ alert_id }) => ({
        url: `/v0/alerts/${alert_id}`,
        method: 'GET'
      })
    })
  })
});

export const { useLazyGetDataminrAlertsQuery, useGetDataminrAlertQuery } = dataminrService;
